import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from 'react';
import { connect } from "react-redux";
import { css } from "styled-components";

// Interface for the props of CancelOrderModal
interface CancelOrderModalProps {
    isOpen: boolean;
    handleClose: () => void;
    onClickCancel: () => void;
    translate: (key: string) => string;
    cancelOrderModalStyles: CancelOrderModalStyles;
}

// Interface for the styles of CancelOrderModalButtonSet
export interface CancelOrderModalStyles {
    buttonsWrapper?: InjectableCss;
    cancelOrderModal?: ModalPresentationProps;
    confirmButtons?: InjectableCss;
    confirmContainer?: InjectableCss;
    cancelButtonCustom?: InjectableCss;
    closeContainer?: InjectableCss;
    confirmMessage?: InjectableCss;
    continueOrderButton?: ButtonPresentationProps;
    spinner?: LoadingSpinnerProps;
}

// Import the cancelOrderModalStyles object
const cancelOrderModalStyles: CancelOrderModalStyles = {
    cancelOrderModal: {
        sizeVariant: "small",
    },
    confirmContainer: {
        css: css`
            &.loader-shown {
                &:before {
                    z-index: 1;
                    content: '';
                    background-color: rgba(9,30,66,0.2);
                    position: absolute;
                    width: 100%;
                    height: 100%;
                    top: 0;
                    left: 0;
                }
            }
        `,
    },
    confirmButtons: {
        css: css`
            display: flex;
            flex-direction: row;
            justify-content: space-between;
            padding-top: 1.625rem;
            margin-top: 1.875rem;
            border-top: 1px solid #D7D7D7;
        `,
    },
    confirmMessage: {
        css: css`
            position: relative;
            h1 {
                color: #002554;
                font-family: 'Roboto', sans-serif;
                font-size: 1.25rem;
                font-weight: 500;
                letter-spacing: 0;
                line-height: 32px;
                position: relative;
                margin-bottom: 2.1875rem;
                margin-top: .5rem;

                &:after {
                    height: 2px;
                    width: 44px;
                    background-color: #C63527;
                    content: '';
                    display: inline-block;
                    position: absolute;
                    bottom: -12px;
                    left: 1px;
                }
            }

            .icon-alert {
                svg {
                    transform: scale(1.5);
                    stroke: #c63527;
                }
            }

            p {
                color: #002554;
                font-family: Lato, sans-serif;
                font-size: .9375rem;
                letter-spacing: 0;
                line-height: 1.3125rem;
            }
            .spinner-container {
                position: absolute;
                text-align: center;
                margin: 0;
                z-index: 999999;
                display: block;
                left: 43%;
                top: 55%;
            }
        `,
    },
    continueOrderButton: {
        buttonType: "solid",
        css: css`
            border-radius: 2px;
            background-color: #002554;
            border: 2px solid #002554;
            color: #fff;
            text-transform: uppercase;

            &:hover,
            &:active {
                background-color: #002554;
                border: 2px solid #002554
            }
            span {
                color: #fff;
            }
        `,
    },
    cancelButtonCustom: {
        css: css`
            color: #002554;
            font-family: Roboto;
            font-size: .9375rem;
            letter-spacing: 0;
            line-height: 1.3125rem;
            text-align: center;
            text-decoration: underline;
            cursor: pointer;
            margin-left: 3em;
            margin-right: 3em;
            justify-content: center;
            width: auto;

            ${({ theme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                        margin-left: 2em;
                        margin-right: 2em;
                        min-width: 100px;
                    `,
                        null,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )};
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    closeContainer: {
        css: css`
           position: absolute;
           right: 1.25rem;
           top: 24px;
           .close-label {
                color: #535E64;
                font-family: Roboto;
                font-size: .875rem;
                letter-spacing: 0.16px;
                line-height: 1;
                display: inline-block;
                margin-right: .625rem;
                vertical-align: middle;
           }
           .icon-close {
                display: inline-block;
                cursor: pointer;
                vertical-align: middle;
                line-height: 1;
                width: 1.125rem;
                height: 1.125rem;
                svg {
                    height: 1.125rem;
                    width: 1.125rem;
                }
            }
        `,
    },
};

const styles = cancelOrderModalStyles;
const mapStateToProps = (state: ApplicationState) => ({
    isCancelling: state.pages.orderDetails.isCanceling,
});

type Props = ReturnType<typeof mapStateToProps> & CancelOrderModalProps;

const CancelOrderModal: React.FC<Props> = ({ isOpen, handleClose, onClickCancel, translate, cancelOrderModalStyles, isCancelling }) => {
    return (
        <Modal
            isOpen={isOpen}
            data-test-selector="cancelOrderModal"
            {...styles.cancelOrderModal}
            handleClose={handleClose}
            className="confirm-remove-modal"
        >


            <Typography as="div" {...styles.confirmContainer} className={`${isCancelling && isCancelling === true ? "loader-shown" : ""}`}
            >
                <Typography as="div" onClick={handleClose} {...styles.closeContainer}>
                    <span className="close-label">Close</span>
                    <Icon className="icon-close" src="X" />
                </Typography>
                <Typography as="div" {...styles.confirmMessage}>
                    <Icon className="icon-alert" src="AlertCircle" />
                    <h1>{translate("Cancel this order?")}</h1>
                    <p>{translate("Are you sure you want to cancel this order?")}</p>
                    {isCancelling && isCancelling === true && (
                        <span className="spinner-container"><LoadingSpinner {...styles.spinner} /></span>
                    )}
                </Typography>
                <Typography as="div" {...styles.confirmButtons}>



                    <Clickable onClick={onClickCancel} {...styles.cancelButtonCustom}>
                        {translate("Cancel Order")}
                    </Clickable>
                    <Button onClick={handleClose} {...styles.continueOrderButton}>
                        {translate("Continue with order")}
                    </Button>
                </Typography>
            </Typography>
        </Modal>
    );
};

export default connect(mapStateToProps)(CancelOrderModal);
